// Must be the first import of src/index.js: KDS resolves its colours at module
// load, and design-tokens colours are shared by reference, so tagging them once
// here covers both KDS and the app. The tag is read by ./toColorString.
import color from '@decisiv/design-tokens/lib/color';
import KDS_TOKEN_MAP from './kdsTokenMap';

export const VAR_KEY = '__var';

// Recovers the variable for a token that lost its tag (hot reload re-evaluated
// design-tokens after this file ran). First tagged name wins, so a token
// sharing its RGB with an earlier one (shadow vs charcoal) resolves by tag only.
const exactIndex = new Map();
const rgbIndex = new Map();
const rgbKey = (c) => `${c.red},${c.green},${c.blue}`;
const exactKey = (c) => `${rgbKey(c)},${c.alpha ?? 1}`;

export const baseVarFor = (c) => rgbIndex.get(rgbKey(c));

// Only design-token-shaped objects (`fg`) are recovered: KDS's rgbaToHex blends
// carry no `fg` and must stay literal even when they land on a token's RGB.
export function varNameFor(c) {
  if (!('fg' in c)) return undefined;
  const exact = exactIndex.get(exactKey(c));
  if (exact) return { name: exact, alpha: c.alpha };
  const base = rgbIndex.get(rgbKey(c));
  return base ? { name: base, alpha: undefined } : undefined;
}

const kebab = (s) => s.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase();

const isColor = (o) => o && typeof o === 'object' && typeof o.red === 'number';

// Enumerable so KDS's `{ ...token, alpha }` copies carry the tag along.
const tag = (token, name) => {
  if (!isColor(token) || !KDS_TOKEN_MAP[name]) return;
  if (!exactIndex.has(exactKey(token))) exactIndex.set(exactKey(token), name);
  if ((token.alpha ?? 1) === 1 && !rgbIndex.has(rgbKey(token))) {
    rgbIndex.set(rgbKey(token), name);
  }
  Object.defineProperty(token, VAR_KEY, {
    value: { name, alpha: token.alpha },
    enumerable: true,
    configurable: true,
    writable: true,
  });
};

// `fg` pairs are shared raw constants: one charcoal, one snow-white.
const tagFg = (token) => {
  if (!isColor(token?.fg)) return;
  tag(
    token.fg,
    token.fg.red === 255 ? '--kds-base-snow-white' : '--kds-base-charcoal',
  );
};

export default function applyThemeBridge() {
  Object.entries(color).forEach(([group, entries]) => {
    Object.entries(entries).forEach(([name, value]) => {
      if (isColor(value)) {
        tag(value, `--kds-${kebab(group)}-${kebab(name)}`);
        tagFg(value);
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([shade, shadeValue]) => {
          tag(shadeValue, `--kds-${kebab(group)}-${kebab(name)}-${shade}`);
          tagFg(shadeValue);
        });
      }
    });
  });
  return color;
}

applyThemeBridge();
